import { createRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { apiClient } from '@/lib/api'
import { useOpenProject } from '@/providers/WerkshopProvider'
import { usePlatform } from '@/hooks/usePlatform'
import { useSequencerContainers } from '@/hooks/useSequencerProjects'
import { timeAgo } from '@/lib/format'
import { NewProjectDialog } from '@/components/projects/NewProjectDialog'

/**
 * Werkshop dashboard — the landing surface for the DAW app.
 *
 * Shows the currently-open project, a recent-projects widget, and
 * quick-launch tiles into each sub-surface (Sequencer / MIDI /
 * Tools / Store / Settings). Power users can skip this and jump
 * straight to `/werkshop/projects` with their last project via
 * the `WerkshopShell` tab strip in the header.
 */
export const werkshopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: WerkshopDashboard,
})

const TILES = [
  {
    to: '/werkshop/projects',
    label: 'Sequencer',
    description: 'Patterns, pads, samples, MIDI out',
  },
  {
    to: '/werkshop/midi',
    label: 'MIDI Router',
    description: 'Patchbay routing for hardware MIDI gear',
  },
  {
    to: '/werkshop/tools',
    label: 'Tools',
    description: 'Sampler editor, Piano Roll, Automation',
  },
  {
    to: '/werkshop/store',
    label: 'Store',
    description: 'Browse and license Werkshop modules',
  },
  {
    to: '/werkshop/settings',
    label: 'Settings',
    description: 'BPM, conform, danger zone — scoped to the open project',
  },
] as const

function WerkshopDashboard() {
  const { projectId } = useOpenProject()
  const platform = usePlatform()
  const { data: containers } = useSequencerContainers()
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  // Three most-recently-updated containers. Useful when the user
  // lands on the dashboard and wants to jump back into a project
  // that isn't the last-open one. Sorted descending by
  // `updatedAt` / `createdAt`; owned + shared entries both
  // qualify.
  const recent = (containers ?? [])
    .slice()
    .sort((a, b) => {
      const ta = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
      const tb = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime()
      return tb - ta
    })
    .slice(0, 3)

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader title="Werkshop" workspaceSwitcher="werkshop" />
      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-4'
            : 'max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6'
        }
      >
        <section className="flex flex-col gap-3">
          <h1 className="text-xl font-semibold text-content-primary">
            {projectId ? 'Pick up where you left off' : 'Start a new project'}
          </h1>
          <p className="text-sm text-content-secondary">
            {projectId
              ? 'Your last-open project is still loaded. Jump back into the sequencer or switch surfaces below.'
              : 'Open or create a project from the Sequencer tab. Every surface below shares the same open project.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setNewProjectOpen(true)}
              className="btn-primary btn-sm"
            >
              + New project
            </button>
            {projectId && (
              <Link
                to="/werkshop/projects/$id"
                params={{ id: projectId }}
                className="btn-secondary btn-sm"
              >
                Resume in Sequencer
              </Link>
            )}
          </div>
        </section>

        {recent.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-xs font-mono uppercase tracking-[0.14em] text-content-tertiary">
              Recent projects
            </h2>
            <div className="grid gap-2 grid-cols-1 sm:grid-cols-3">
              {recent.map(c => (
                <Link
                  key={c.id}
                  to="/werkshop/projects/$id"
                  params={{ id: c.id }}
                  className="rounded-lg border border-brd-subtle bg-surface-raised p-3 hover:border-accent transition-colors flex flex-col gap-0.5"
                >
                  <span className="text-sm font-medium text-content-primary truncate">
                    {c.name}
                  </span>
                  <span className="text-[10px] font-mono text-content-tertiary">
                    {c.updatedAt
                      ? timeAgo(c.updatedAt)
                      : c.createdAt
                        ? timeAgo(c.createdAt)
                        : 'unknown'}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section
          aria-label="Werkshop surfaces"
          className="grid gap-3 grid-cols-1 sm:grid-cols-2"
        >
          {TILES.map(tile => (
            <Link
              key={tile.to}
              to={tile.to}
              className="group rounded-xl border border-brd-subtle bg-surface-raised p-4 hover:border-accent transition-colors"
            >
              <div className="text-sm font-semibold text-content-primary group-hover:text-accent">
                {tile.label}
              </div>
              <div className="text-xs text-content-secondary mt-1">
                {tile.description}
              </div>
            </Link>
          ))}
        </section>
      </main>
      <NewProjectDialog
        isOpen={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
      />
    </div>
  )
}
